import { useState, useEffect } from 'react'
import { Save, Search, ChevronDown, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useGetEmployeeListQuery,
  useSelectSheetProjectsQuery,
  useBulkCreateProjectDetailsMutation,
  type SelectSheetProject,
} from '@/redux/service/project'
import { useLazyGetClientsQuery } from '@/redux/service/client'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { useFormik, FormikErrors, FormikTouched } from 'formik'
import * as Yup from 'yup'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Calendar } from '@/components/ui/calendar'
import { format } from 'date-fns'
import { Calendar as CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import toast from 'react-hot-toast'

const HEADERS = [
  'Setup',
  'Project Name',
  'Project Type',
  'Client',
  'Project Leader',
  'Budget ($)',
  'Total Hours',
  'Hourly Cost ($)',
  'Start Date',
  'End Date',
  'Description',
]

const PROJECT_TYPE_OPTIONS = [
  'Upwork Tracking',
  'Outside Fix',
  'Internal',
  'Upwork Fix',
]

const KEYS = [
  'setupCompleted',
  'projectName',
  'projectType',
  'client',
  'projectLeader',
  'budget',
  'totalHours',
  'hourlyCost',
  'startDate',
  'endDate',
  'description',
]

interface ClientOption {
  value: string | number
  label: string
  projectIds?: string[]
}

interface SheetRow {
  projectId: string
  setupCompleted: boolean
  projectName: string
  projectType: string
  client: ClientOption | null
  projectLeader: string
  budget: string
  totalHours: string
  hourlyCost: string
  startDate: string
  endDate: string
  description: string
}

const validationSchema = Yup.object().shape({
  rows: Yup.array().of(
    Yup.object().shape({
      projectId: Yup.string(),
      projectName: Yup.string(),
      projectType: Yup.string().when('projectId', {
        is: (val: string) => val && val.length > 0,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.optional(),
      }),
      projectLeader: Yup.string().when('projectId', {
        is: (val: string) => val && val.length > 0,
        then: schema => schema.required('Required'),
        otherwise: schema => schema.optional(),
      }),
      budget: Yup.number().when('projectId', {
        is: (val: string) => val && val.length > 0,
        then: schema =>
          schema
            .typeError('Must be a number')
            .min(1, 'Min 1')
            .required('Required'),
        otherwise: schema => schema.optional(),
      }),
      totalHours: Yup.number().when('projectId', {
        is: (val: string) => val && val.length > 0,
        then: schema =>
          schema
            .typeError('Must be a number')
            .min(1, 'Min 1')
            .required('Required'),
        otherwise: schema => schema.optional(),
      }),
      hourlyCost: Yup.number().when('projectId', {
        is: (val: string) => val && val.length > 0,
        then: schema =>
          schema.typeError('Must be a number').required('Required'),
        otherwise: schema => schema.optional(),
      }),
      client: Yup.mixed().nullable(),
      startDate: Yup.date().nullable(),
      endDate: Yup.date()
        .nullable()
        .min(Yup.ref('startDate'), 'End date cannot be before start date'),
      description: Yup.string().nullable(),
    })
  ),
})

const Sheet = () => {
  const [openRowIndex, setOpenRowIndex] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data: sheetsData, isLoading: isProjectsLoading } =
    useSelectSheetProjectsQuery({ search: debouncedSearch })

  const [searchClients, { isFetching: isSearchingClients }] =
    useLazyGetClientsQuery()
  const { data: employeeList, isLoading: isEmployeesLoading } =
    useGetEmployeeListQuery()

  const [bulkCreateProjectDetails, { isLoading: isSaving }] =
    useBulkCreateProjectDetailsMutation()

  const formik = useFormik<{ rows: SheetRow[] }>({
    initialValues: {
      rows: Array.from({ length: 10 }, () => ({
        projectName: '',
        projectType: '',
        client: null,
        projectLeader: '',
        budget: '',
        totalHours: '',
        hourlyCost: '',
        startDate: '',
        endDate: '',
        description: '',
        projectId: '',
        setupCompleted: false,
      })),
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      const dataToSave = values.rows
        .filter(row => row.projectId)
        .map(row => ({
          project_id: Number(row.projectId),
          project_type: row.projectType,
          assignee_username: row.projectLeader,
          client: row.client || null,
          total_budget: row.budget,
          total_hours: Number(row.totalHours),
          hourly_cost: Number(row.hourlyCost),
          start_date: row.startDate
            ? format(new Date(row.startDate), 'yyyy-MM-dd')
            : null,
          end_date: row.endDate
            ? format(new Date(row.endDate), 'yyyy-MM-dd')
            : null,
          description: row.description,
          setup_completed: row.setupCompleted,
        }))

      if (dataToSave.length === 0) {
        toast.error('Please select at least one project.')
        return
      }

      try {
        await bulkCreateProjectDetails(dataToSave).unwrap()
        toast.success('Sheet data saved successfully!')
        resetForm()
      } catch (err) {
        const error = err as { data?: { message?: string } }
        toast.error(error?.data?.message ?? 'Failed to save sheet data.')
      }
    },
  })

  const { values, setFieldValue, handleSubmit, errors, touched } = formik

  const calculateRowValues = (row: SheetRow): SheetRow => {
    const projectType = row.projectType
    if (projectType === 'Upwork Tracking') {
      const hours = Number(row.totalHours)
      const cost = Number(row.hourlyCost)
      if (!isNaN(hours) && !isNaN(cost) && hours > 0 && cost > 0) {
        return { ...row, budget: (hours * cost).toFixed(2) }
      }
    } else if (
      ['Upwork Fix', 'Internal', 'Outside Fix'].includes(projectType)
    ) {
      const budgetVal = Number(row.budget)
      const hours = Number(row.totalHours)
      if (!isNaN(budgetVal) && !isNaN(hours) && hours > 0) {
        return { ...row, hourlyCost: (budgetVal / hours).toFixed(2) }
      }
    }
    return row
  }

  const handleCellChange = (
    rowIndex: number,
    colIndex: number,
    value: string | ClientOption | null | boolean
  ) => {
    const key = KEYS[colIndex] as keyof SheetRow
    const rows = [...values.rows]
    const row = { ...rows[rowIndex] }

    // Update the specific field
    if (key === 'client') {
      row.client = value as ClientOption | null
    } else if (key === 'setupCompleted') {
      row.setupCompleted = value as unknown as boolean
    } else {
      ;(row[key] as string) = (value as string) || ''
    }

    const updatedRow = calculateRowValues(row as SheetRow)

    const syncKeys: (keyof SheetRow)[] = [
      'projectType',
      'client',
      'projectLeader',
      'setupCompleted',
    ]
    if (syncKeys.includes(key) && updatedRow.projectId) {
      const newRows = values.rows.map((r: SheetRow, idx: number) => {
        if (idx === rowIndex) return updatedRow
        if (r.projectId === updatedRow.projectId) {
          const syncUpdate: SheetRow = { ...r }
          if (key === 'client') {
            syncUpdate.client = value as ClientOption | null
          } else if (key === 'setupCompleted') {
            syncUpdate.setupCompleted = value as unknown as boolean
          } else {
            ;(syncUpdate[key] as string) = (value as string) || ''
          }
          return calculateRowValues(syncUpdate as SheetRow)
        }
        return r
      })
      setFieldValue('rows', newRows)
    } else {
      setFieldValue(`rows[${rowIndex}]`, updatedRow)
    }

    if (
      rowIndex === values.rows.length - 1 &&
      value !== '' &&
      values.rows.length < 100
    ) {
      setFieldValue(`rows`, [
        ...values.rows,
        {
          projectName: '',
          projectType: '',
          client: null,
          projectLeader: '',
          budget: '',
          totalHours: '',
          hourlyCost: '',
          startDate: '',
          endDate: '',
          description: '',
          projectId: '',
          setupCompleted: false,
        },
      ])
    }
  }

  const handleProjectSelect = (
    rowIndex: number,
    project: SelectSheetProject
  ) => {
    const row = { ...values.rows[rowIndex] }
    const projectId = project.project_id.toString()

    row.projectId = projectId
    row.projectName = project.list_name
    row.projectType = project.project_type || ''
    row.client = project.client_id
      ? { value: String(project.client_id), label: project.client_name || '' }
      : null
    row.projectLeader = project.assignee_username || ''
    row.setupCompleted = project.setup_completed || false

    const updatedRow = calculateRowValues(row as SheetRow)

    // Sync with other rows having the same projectId
    const newRows = values.rows.map((r: SheetRow, idx: number) => {
      if (idx === rowIndex) return updatedRow
      if (r.projectId === projectId) {
        return calculateRowValues({
          ...r,
          projectType: updatedRow.projectType,
          client: updatedRow.client,
          projectLeader: updatedRow.projectLeader,
          setupCompleted: updatedRow.setupCompleted,
        } as SheetRow)
      }
      return r
    })

    setFieldValue('rows', newRows)

    if (rowIndex === values.rows.length - 1 && values.rows.length < 100) {
      const emptyRow: SheetRow = {
        projectName: '',
        projectType: '',
        client: null,
        projectLeader: '',
        budget: '',
        totalHours: '',
        hourlyCost: '',
        startDate: '',
        endDate: '',
        description: '',
        projectId: '',
        setupCompleted: false,
      }
      setFieldValue(`rows`, [...values.rows, emptyRow])
    }
  }

  const loadClientOptions = (inputValue: string) => {
    return searchClients({
      search: inputValue,
      page: 1,
      limit: 20,
    })
      .unwrap()
      .then(response =>
        response.data.map(client => ({
          value: client.client_id,
          label: client.client_name,
          projectIds: client.projects?.map(p => p.project_id) ?? [],
        }))
      )
  }

  const handleSave = () => {
    handleSubmit()
  }

  return (
    <div className='flex flex-col h-full bg-background text-foreground min-h-screen'>
      {/* Top Header Section */}
      <div className='flex flex-shrink-0 items-center justify-between px-6 py-4 border-b border-[#1f1f1f] bg-background'>
        <div>
          <h1 className='text-xl font-semibold text-white tracking-tight'>
            Data Sheet
          </h1>
          <p className='text-sm text-slate-500 mt-1'>
            Add or modify entries in the grid below.
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className='gap-2 shadow-sm bg-primary hover:bg-primary/90 text-black border-0 font-bold'
        >
          <Save className='h-4 w-4' />
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      <div className='flex-1 overflow-auto bg-[#0a0a0a] p-6'>
        <div className='min-w-max border border-[#1f1f1f] rounded-lg bg-[#0a0a0a] shadow-sm overflow-hidden'>
          <table className='w-full text-sm border-collapse'>
            <thead className='bg-[#0a0a0a] uppercase text-[11px] font-bold tracking-wider text-slate-400 select-none'>
              <tr>
                <th className='sticky top-0 z-20 w-14 border-b border-r border-[#1f1f1f] bg-[#0a0a0a] py-3 px-2 text-center shadow-[0_1px_0_0_#1f1f1f]'>
                  #
                </th>
                {HEADERS.map((header, idx) => (
                  <th
                    key={idx}
                    className='sticky top-0 z-20 border-b border-r border-[#1f1f1f] bg-[#0a0a0a] px-4 py-3 text-left shadow-[0_1px_0_0_#1f1f1f]'
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='text-white'>
              <TooltipProvider>
                {values.rows.map((row: SheetRow, rowIndex: number) => (
                  <tr
                    key={rowIndex}
                    className='group border-b border-[#1f1f1f] hover:bg-primary/5 transition-colors'
                  >
                    <td className='border-r border-[#1f1f1f] bg-[#1a1a1a]/50 py-2 px-2 text-center text-xs text-slate-500 font-medium select-none w-14 group-hover:bg-primary/10 group-hover:text-primary transition-colors'>
                      {rowIndex + 1}
                    </td>
                    {KEYS.map((key, colIndex) => {
                      const rawValue = row[key as keyof SheetRow]
                      const rowErrors = errors.rows as
                        FormikErrors<SheetRow>[] | undefined
                      const error =
                        rowErrors?.[rowIndex]?.[key as keyof SheetRow]
                      const rowTouched = touched.rows as
                        FormikTouched<SheetRow>[] | undefined
                      const isTouched =
                        rowTouched?.[rowIndex]?.[key as keyof SheetRow]

                      const isProjectMissing = colIndex !== 1 && !row.projectId

                      const cellWrapperClass = `p-0 border-r border-[#1f1f1f] relative bg-[#0a0a0a] transition-all ${
                        error && isTouched
                          ? 'ring-2 ring-inset ring-red-500 z-10'
                          : ''
                      } ${isProjectMissing ? 'bg-[#1a1a1a]/50 cursor-not-allowed' : ''}`

                      const renderError = () => {
                        if (error && isTouched && !isProjectMissing) {
                          return (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className='absolute right-2 top-1/2 -translate-y-1/2 text-red-500 pointer-events-none'>
                                  <AlertCircle className='h-4 w-4' />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className='bg-red-600 text-white border-red-600'>
                                <p>{error}</p>
                              </TooltipContent>
                            </Tooltip>
                          )
                        }
                        return null
                      }

                      if (colIndex === 0) {
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} w-16 text-center`}
                          >
                            <div className='flex items-center justify-center h-full'>
                              <input
                                type='checkbox'
                                checked={rawValue as boolean}
                                disabled={isProjectMissing}
                                onChange={e =>
                                  handleCellChange(
                                    rowIndex,
                                    colIndex,
                                    e.target.checked
                                  )
                                }
                                className='h-4 w-4 rounded border-[#262626] bg-[#1a1a1a] text-primary focus:ring-primary transition-all cursor-pointer'
                              />
                            </div>
                            {renderError()}
                          </td>
                        )
                      }

                      if (colIndex === 1) {
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[250px]`}
                          >
                            <Popover
                              open={openRowIndex === rowIndex}
                              onOpenChange={open =>
                                setOpenRowIndex(open ? rowIndex : null)
                              }
                            >
                              <PopoverTrigger asChild>
                                <button className='w-full h-full min-h-[40px] px-4 py-2.5 text-left text-sm flex items-center justify-between transition-all font-medium text-white focus:bg-primary/5 focus:z-10 focus:outline-none border-2 border-transparent hover:bg-white/[0.02]'>
                                  <span className='truncate flex-1 text-slate-300'>
                                    {typeof rawValue === 'string'
                                      ? rawValue || 'Select Project...'
                                      : 'Select Project...'}
                                  </span>
                                  <ChevronDown className='h-4 w-4 opacity-50 flex-shrink-0 ml-2' />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                className='w-[300px] p-0 shadow-lg border-[#1f1f1f] bg-[#0a0a0a]'
                                align='start'
                              >
                                <div className='flex items-center border-b border-[#1f1f1f] px-3'>
                                  <Search className='mr-2 h-4 w-4 shrink-0 opacity-50 text-slate-500' />
                                  <input
                                    className='flex h-11 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50 text-white'
                                    placeholder='Search Project...'
                                    value={search}
                                    onChange={e => setSearch(e.target.value)}
                                  />
                                </div>
                                <div className='max-h-[300px] overflow-y-auto p-1 bg-[#0a0a0a]'>
                                  {isProjectsLoading ? (
                                    <div className='p-4 text-center text-sm text-slate-500'>
                                      Loading...
                                    </div>
                                  ) : sheetsData?.data?.length === 0 ? (
                                    <div className='p-4 text-center text-sm text-slate-500'>
                                      No projects found.
                                    </div>
                                  ) : (
                                    sheetsData?.data?.map(p => (
                                      <div
                                        key={p.project_id}
                                        role='button'
                                        className='relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-none hover:bg-primary/10 hover:text-primary font-medium transition-colors text-slate-300'
                                        onClick={() => {
                                          handleProjectSelect(rowIndex, p)
                                          setOpenRowIndex(null)
                                        }}
                                      >
                                        {p.list_name}
                                      </div>
                                    ))
                                  )}
                                </div>
                              </PopoverContent>
                            </Popover>
                            {renderError()}
                          </td>
                        )
                      }

                      if (colIndex === 2) {
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[200px]`}
                          >
                            <Select
                              value={(rawValue as string) || ''}
                              disabled={isProjectMissing}
                              onValueChange={val =>
                                handleCellChange(rowIndex, colIndex, val)
                              }
                            >
                              <SelectTrigger
                                className={cn(
                                  'w-full h-full min-h-[40px] rounded-none border-0 bg-transparent px-4 py-2.5 text-sm font-medium focus:ring-0 focus:ring-offset-0 focus:outline-none focus:bg-primary/5 focus:z-10 text-slate-300 shadow-none',
                                  isProjectMissing && 'opacity-50'
                                )}
                              >
                                <SelectValue placeholder='Select Type...' />
                              </SelectTrigger>
                              <SelectContent className='z-[100] bg-[#0a0a0a] border-[#1f1f1f] text-white'>
                                {PROJECT_TYPE_OPTIONS.map(opt => (
                                  <SelectItem key={opt} value={opt}>
                                    {opt}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {renderError()}
                          </td>
                        )
                      }
                      if (colIndex === 3) {
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[280px]`}
                          >
                            <div
                              className={cn(
                                'space-y-2 col-span-1 md:col-span-2',
                                isProjectMissing &&
                                  'opacity-50 pointer-events-none'
                              )}
                            >
                              <AsyncCreatableSelect
                                cacheOptions
                                defaultOptions
                                loadOptions={loadClientOptions}
                                isDisabled={isProjectMissing}
                                isClearable
                                isLoading={isSearchingClients}
                                value={
                                  (rawValue as ClientOption | null) || null
                                }
                                onChange={newValue =>
                                  handleCellChange(
                                    rowIndex,
                                    colIndex,
                                    newValue as ClientOption | null
                                  )
                                }
                                onBlur={() =>
                                  formik.setFieldTouched(
                                    `rows[${rowIndex}].client`,
                                    true
                                  )
                                }
                                className='text-sm'
                                classNames={{
                                  control: () =>
                                    '!bg-[#0a0a0a] !border-none !min-h-[40px] !rounded-none',
                                  menu: () => '!bg-[#1a1a1a] !border-[#1f1f1f]',
                                  option: state =>
                                    state.isFocused
                                      ? '!bg-primary/10 !text-primary'
                                      : '!text-slate-300',
                                  singleValue: () => '!text-slate-300',
                                  input: () => '!text-slate-300',
                                }}
                                placeholder='Search or create a client...'
                              />
                            </div>
                            {renderError()}
                          </td>
                        )
                      }
                      if (colIndex === 4) {
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[280px]`}
                          >
                            <div
                              className={cn(
                                'space-y-2 border-none',
                                isProjectMissing &&
                                  'opacity-50 pointer-events-none'
                              )}
                            >
                              <Select
                                value={(rawValue as string) || ''}
                                disabled={isProjectMissing}
                                onValueChange={value =>
                                  handleCellChange(rowIndex, colIndex, value)
                                }
                              >
                                <SelectTrigger className='border-none h-11 bg-transparent text-slate-300 focus:bg-primary/5 rounded-none'>
                                  <SelectValue placeholder='Select Leader...' />
                                </SelectTrigger>
                                <SelectContent className='bg-[#1a1a1a] border-[#1f1f1f] text-white'>
                                  {isEmployeesLoading ? (
                                    <SelectItem value='loading' disabled>
                                      Loading Employees...
                                    </SelectItem>
                                  ) : (employeeList?.data ?? []).length > 0 ? (
                                    (employeeList?.data ?? []).map(
                                      (user: {
                                        user_id: number
                                        user_name: string
                                      }) => (
                                        <SelectItem
                                          key={user.user_id}
                                          value={user.user_name}
                                        >
                                          {user.user_name}
                                        </SelectItem>
                                      )
                                    )
                                  ) : (
                                    <SelectItem value='no-users' disabled>
                                      No Employee found
                                    </SelectItem>
                                  )}
                                </SelectContent>
                              </Select>
                            </div>
                            {renderError()}
                          </td>
                        )
                      }
                      if (colIndex === 5 || colIndex === 6 || colIndex === 7) {
                        const isDisabled =
                          !row.projectId ||
                          (colIndex === 5 &&
                            row.projectType === 'Upwork Tracking') ||
                          (colIndex === 7 &&
                            ['Upwork Fix', 'Internal', 'Outside Fix'].includes(
                              row.projectType
                            ))

                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[120px] ${
                              isDisabled ? 'bg-[#1a1a1a]' : 'bg-[#0a0a0a]'
                            }`}
                          >
                            <input
                              type='number'
                              min='1'
                              value={(rawValue as string) || ''}
                              disabled={isDisabled}
                              onChange={e =>
                                handleCellChange(
                                  rowIndex,
                                  colIndex,
                                  e.target.value
                                )
                              }
                              onBlur={e => {
                                formik.setFieldTouched(
                                  `rows[${rowIndex}].${key}`,
                                  true
                                )
                                const val = Number(e.target.value)
                                if (
                                  val < 1 &&
                                  e.target.value !== '' &&
                                  !isDisabled
                                ) {
                                  handleCellChange(rowIndex, colIndex, '1')
                                }
                              }}
                              className={`w-full h-full px-4 py-2.5 bg-transparent border-2 border-transparent outline-none focus:border-primary focus:bg-primary/5 focus:z-10 relative text-sm transition-all font-medium placeholder:text-slate-700 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                isDisabled
                                  ? 'cursor-not-allowed opacity-70'
                                  : ''
                              }`}
                            />
                            {renderError()}
                          </td>
                        )
                      }
                      if (colIndex === 8 || colIndex === 9) {
                        const dateValue = rawValue
                          ? new Date(rawValue as string)
                          : undefined
                        return (
                          <td
                            key={colIndex}
                            className={`${cellWrapperClass} min-w-[150px]`}
                          >
                            <Popover>
                              <PopoverTrigger
                                asChild
                                disabled={isProjectMissing}
                              >
                                <button
                                  className={cn(
                                    'w-full h-full px-4 py-2.5 text-left text-sm font-medium flex items-center justify-between transition-all hover:bg-white/[0.02]',
                                    !rawValue && 'text-slate-600',
                                    isProjectMissing && 'opacity-50'
                                  )}
                                  disabled={isProjectMissing}
                                >
                                  {rawValue ? (
                                    format(new Date(rawValue as string), 'PPP')
                                  ) : (
                                    <span>Pick a date</span>
                                  )}
                                  <CalendarIcon className='ml-2 h-4 w-4 opacity-50' />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent
                                className='w-auto p-0'
                                align='start'
                              >
                                <Calendar
                                  mode='single'
                                  captionLayout='dropdown'
                                  fromYear={2018}
                                  toYear={2035}
                                  selected={dateValue}
                                  onSelect={date => {
                                    handleCellChange(
                                      rowIndex,
                                      colIndex,
                                      date ? date.toISOString() : ''
                                    )
                                  }}
                                  disabled={
                                    colIndex === 9 && row.startDate
                                      ? date =>
                                          date <
                                          new Date(row.startDate as string)
                                      : undefined
                                  }
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                            {renderError()}
                          </td>
                        )
                      }
                      return (
                        <td
                          key={colIndex}
                          className={`${cellWrapperClass} min-w-[120px] last:border-r-0`}
                        >
                          <input
                            type='text'
                            value={(rawValue as string) || ''}
                            disabled={isProjectMissing}
                            onChange={e =>
                              handleCellChange(
                                rowIndex,
                                colIndex,
                                e.target.value
                              )
                            }
                            onBlur={() =>
                              formik.setFieldTouched(
                                `rows[${rowIndex}].${key}`,
                                true
                              )
                            }
                            className={cn(
                              'w-full h-full px-4 py-2.5 bg-transparent border-2 border-transparent outline-none focus:border-green-500 focus:bg-green-50 focus:z-10 relative text-sm transition-all font-medium placeholder:text-zinc-300 text-black',
                              isProjectMissing &&
                                'cursor-not-allowed opacity-50'
                            )}
                            placeholder={
                              rowIndex === 0 &&
                              colIndex === 1 &&
                              Object.values(row).every(val => val === '')
                                ? '...'
                                : ''
                            }
                          />
                          {renderError()}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </TooltipProvider>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default Sheet
